import ApplicationController from '@aha-app/mvc';
import type WhiteboardController from '../../controllers/WhiteboardController';
import {
  hasEmbeddedTextBehaviors,
  hasHighlightBehaviors,
  hasRecordLinks,
  isShapeIntegrationRecord,
  isShapeRecord,
  isSimpleGroup,
  isTable,
  isTextField,
  isWireframe,
} from '../../objects/guards';
import type HighlightBehaviors from '../../objects/HighlightBehaviors';
import { zoomAndPanTo } from '../../utils/zoom';
import styles from './SearchBar.module.css';
import { findTextMatch } from './text';

export const getTextContent = (obj: fabric.Object): string[] => {
  if (isShapeIntegrationRecord(obj)) {
    const text = obj.integrationRecord?.recordData?.name;
    if (text) {
      return [text];
    }
  }

  const texts: string[] = [];
  if (hasEmbeddedTextBehaviors(obj) || isWireframe(obj)) {
    const plainText = obj.plainText();
    if (plainText) {
      texts.push(plainText);
    }
  }

  if (
    isShapeRecord(obj) &&
    hasRecordLinks(obj.shape.recordInfo) &&
    'referenceNum' in obj.shape.recordInfo?.attributes
  ) {
    const referenceNum = obj.shape.recordInfo?.attributes?.referenceNum;
    if (referenceNum) {
      texts.push(referenceNum);
    }
  }

  return texts;
};

const searchObjects = (objects: fabric.Object[], term: string): Result[] => {
  const sanitisedTerm = term.toLowerCase().trim();

  if (sanitisedTerm.length < 1) {
    return [];
  }

  const results = objects.reduce<Result[]>((acc, obj) => {
    if (isSimpleGroup(obj)) {
      return acc.concat(searchObjects(obj.getObjects(), term));
    }

    if (isTable(obj)) {
      return acc.concat(searchObjects(Object.values(obj._textFields), term));
    }

    const texts = getTextContent(obj);

    if (!texts.length) {
      return acc;
    }

    for (const text of texts) {
      const preview = findTextMatch(text, sanitisedTerm);

      if (preview) {
        acc.push({
          preview,
          object: obj,
        });
      }
    }

    return acc;
  }, []);

  // sort roughly top left to bottom right
  results.sort(
    (a, b) => a.object.left + a.object.top - (b.object.left + b.object.top)
  );

  return results;
};

interface Result {
  preview: { before: string; match: string; after: string };
  object: fabric.Object;
}

interface State {
  searchTerm?: string;
  results?: Result[];
  rawIndex?: number;
}

interface Props {
  whiteboardController: WhiteboardController;
}

class SearchBarController extends ApplicationController<State, Props> {
  private highlighted: HighlightBehaviors[] = [];
  resultsEl: HTMLDivElement | undefined;

  getCurrentIndex() {
    const { results, rawIndex } = this.state;

    if (rawIndex === undefined || !results) {
      return;
    }

    return (results.length + rawIndex) % results.length;
  }

  selectCurrentResult() {
    const { results } = this.state;
    const { whiteboardController } = this.props;

    const index = this.getCurrentIndex();

    if (!results || index === undefined || !results[index]) {
      return;
    }

    const result = results[index];

    void zoomAndPanTo(whiteboardController, [result.object], {
      marginFactor: 0.5,
      maxZoom: 1,
      onComplete: () => {
        setTimeout(
          () => {
            whiteboardController.canvas
              .setActiveObject(selectTarget)
              .requestRenderAll();
          },
          // small extra delay to avoid jank
          100
        );
      },
    });

    const selectTarget = isTextField(result.object)
      ? result.object.parent
      : result.object;

    this.resultsEl?.children[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });

    // when we navigate to a result, switch to highlighting the current one
    this.resultsEl?.classList.add(styles.isNavigating);
  }

  actionSetResults(results: Result[]) {
    this.state.results = results;
    this.state.rawIndex = undefined;
  }

  actionUpdateTerm(term: string) {
    this.state.searchTerm = term;
    this.actionUpdateResults();
  }

  actionHide() {
    this.props.whiteboardController.actionHideSearchBar();
  }

  highlightResults() {
    this.clearHighlights();
    if (!this.state.results) return;

    for (const result of this.state.results) {
      const object = result.object;

      // if object.canvas is undefined, the object must have been deleted
      if (object && hasHighlightBehaviors(object) && object.canvas) {
        object.highlight();
        this.highlighted.push(object);
      }
    }
  }

  clearHighlights() {
    for (const obj of this.highlighted) {
      obj.clearHighlight();
    }
    this.highlighted = [];
  }

  actionSetCurrent(i: number) {
    this.state.rawIndex = i;
    this.selectCurrentResult();
  }

  actionNext() {
    const { rawIndex, results } = this.state;

    this.state.rawIndex =
      rawIndex === undefined ? 0 : (rawIndex + 1) % results!.length;
    this.selectCurrentResult();
  }

  actionPrevious() {
    const { rawIndex, results } = this.state;

    this.state.rawIndex = ((rawIndex ?? 0) - 1) % results!.length;
    this.selectCurrentResult();
  }

  private searchTimeout: ReturnType<typeof setTimeout> | undefined;

  actionUpdateResults() {
    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      const currentIndex = this.getCurrentIndex();
      const currentHighlight =
        currentIndex !== undefined
          ? this.state.results?.[currentIndex]?.object
          : undefined;

      const results = searchObjects(
        this.props.whiteboardController.canvas.getObjects(),
        this.state.searchTerm ?? ''
      );

      this.state.results = results;

      this.highlightResults();

      if (currentHighlight) {
        const newIndex = results.findIndex(
          result => result.object.id === currentHighlight.id
        );
        if (newIndex !== -1) {
          // keep the same element as current if it's still visible in the list
          this.state.rawIndex = newIndex;
        } else {
          // otherwise reset the current element
          this.state.rawIndex = undefined;
        }
      }
    }, 100);
  }
}

export default SearchBarController;
